import {
  DepositBridgeAdapterPayloadType,
  INTERNAL_SYMBOL,
  KatanaPerpsDeposit,
  KatanaPerpsWithdrawal,
  RestRequestCancelOrders,
  RestRequestGetFundingPayments,
  RestRequestGetHistoricalPnL,
  RestRequestSetVaultDetails,
  RestResponseGetMarkets,
  SignTypedData,
  encodeDepositBridgeAdapterPayload,
  encodeWithdrawalBridgeAdapterPayload,
} from '@katanaperps/katana-perps-sdk';
import { v1 as uuidv1 } from 'uuid';

import { getStargateBridgeConfig } from 'components/balances/bridge/crossChainMapping';
import config from 'config';
import { ChainsFromConfig } from 'config/configType';
import {
  DEFAULT_USDC_DECIMALS,
  ETH_ADDRESS_ZERO,
  EXCHANGE_DECIMALS,
  IGNORED_NONCE,
  PayoutProgramCode,
} from 'config/constants';
import { BigNumberJs } from 'lib/BigNumber';
import { clearGlobalEnforcedChainId } from 'lib/ethereum/chains';
import {
  TypeDataSignFn,
  signTypedData,
} from 'lib/ethereum/sign/typedData/eip712';
import { Chains } from 'reduxState/exchange/exchangeActiveChainUtils';
import { IWallet } from 'reduxState/wallet/walletReducer';

import {
  getChainPrivateApi,
  getChainPublicApi,
  getMainPrivateApi,
} from './apiCoreSdk';
import {
  prepareCreateLimitOrder,
  prepareCreateLimitStopOrder,
  prepareCreateMarketOrder,
} from './apiExchangePlaceOrder';
import {
  includeBaseAndQuoteFromMarket,
  filterInactiveOrders,
} from './apiUtils';
import {
  ApiTrade,
  ApiWithdrawal,
  ApiOrder,
  ApiExchange,
  ApiTicker,
  ApiDeposit,
  ApiCandleInterval,
  ApiBalanceHistory,
  ApiInternalCandles,
  ApiFillsPaginatedRaw,
} from './types';

// import { formatFileFriendlyDateDay } from 'lib/date';
import type { DelegatedKeyAuthorization } from 'reduxState/delegatedKey/authorizationReducer';

export const fetchMarkets = () => getChainPublicApi().getMarkets();

export const fetchMarket = (market: string): Promise<RestResponseGetMarkets> =>
  getChainPublicApi().getMarkets({ market });

export const fetchExchangeInfo = (
  /** Required to reload cause it contains contract address */
  activeChain: Chains,
  /** Required for depositEnabled and withdrawEnabled fields which are wallet dependent */
  _walletAddress: string | undefined
): Promise<ApiExchange> => getChainPublicApi(activeChain).getExchange();

export const fetchMarketTicker = async (
  marketSymbol: string
): Promise<ApiTicker | undefined> => {
  const tickers = await getChainPrivateApi().getTickers(marketSymbol);
  return tickers[0];
};

export const fetchQuoteTickers = (quote: string): Promise<ApiTicker[]> =>
  getChainPrivateApi().getTickersByQuote(quote);

// To fetch orderbook we use Orderbook SDK instead (with autoamtic live update)
// export const fetchLevel2OrderBook = (
//   marketSymbol: string,
//   numberOfOrdersPerSide: number
// ): Promise<ApiLevelTwoOrderBook> =>
//   getChainPublicApi().getOrderBookLevel2(marketSymbol, numberOfOrdersPerSide);

/** Fetch order information and also all order fills (replacement for `fetchOrderFills`) */
export const fetchOrderDetail = (
  walletAddress: string,
  orderId: string
): Promise<ApiOrder> =>
  getChainPrivateApi()
    .getOrder({
      orderId,
      nonce: IGNORED_NONCE,
      wallet: walletAddress,
    })
    .then(includeBaseAndQuoteFromMarket);

export const fetchAllUserActiveOrders = (wallet: string): Promise<ApiOrder[]> =>
  getChainPrivateApi()
    .getOrders({
      nonce: IGNORED_NONCE,
      wallet: wallet,
      closed: false,
      limit: 300, // Note: 250 is maximum user should be able to place
    })
    .then(orders =>
      filterInactiveOrders(orders).map(includeBaseAndQuoteFromMarket).reverse()
    );

export const fetchPublicTrades = (
  marketSymbol: string,
  count = 100
): Promise<ApiTrade[]> =>
  getChainPublicApi().getTrades({
    market: marketSymbol,
    limit: count,
  });

// export const downloadInternalBalanceHistoryCSV = async (wallet: string) => {
//   const csvLink = getChainPrivateApi().getInternalBalanceHistoryCsvLink({
//     wallet,
//   });
//   window.open(csvLink, '_blank');
// };

// export const downloadInternalFillHistoryCSV = async (wallet: string) => {
//   // if (!window) {
//   //   return;
//   // }
//   // const csv = await getChainPrivateApi().getInternalFillHistoryCSV({ wallet });
//   // const blob = new Blob([csv], { type: 'text/csv' });
//   // const url = window.URL.createObjectURL(blob);
//   // // window.open(url);

//   // // We can open directly, but we lose it's name
//   // // Need to create proper link and emulate click...
//   // const downloadLink = document.createElement('a');
//   // downloadLink.href = url;

//   // const chainTitle = chainsMap[chain]?.title || '-';

//   // downloadLink.download = `Idex-${chainTitle}-Trades-${formatFileFriendlyDateDay(
//   //   new Date()
//   // )}-${wallet}.csv`;
//   // document.body.appendChild(downloadLink); // Required for FF
//   // downloadLink.click();
//   // setTimeout(() => {
//   //   downloadLink.remove();
//   // }, 1000);

//   // Alternativelly we can open it directly, but we lose internal header
//   // But we might prevent some memory/performance issues with very large trades listings
//   const csvLink = getChainPrivateApi().getInternalFillHistoryCsvLink({
//     wallet,
//   });
//   window.open(csvLink, '_blank');
// };

export const fetchUserFillsPaginated = ({
  wallet,
  isVault,
  page,
  limit,
  market,
}: {
  wallet: string;
  isVault: boolean;
  page: number;
  limit: number;
  market?: string;
}): Promise<ApiFillsPaginatedRaw> =>
  getChainPrivateApi().getInternalFills({
    wallet,
    isVault,
    page,
    limit,
    market: market || undefined,
  });

export const MAX_CANDLES_INTERNAL_LIMIT = 15_000;

export const fetchTradesOhlcvBars = ({
  marketSymbol,
  resolution,
  countBack,
  start,
  end,
  limit,
}: {
  marketSymbol: string; // BASE-QUOTE
  resolution: ApiCandleInterval;
  countBack: number;
  start?: number;
  end: number;
  limit: number;
}): Promise<ApiInternalCandles> => {
  return getChainPrivateApi().getInternalCandles({
    market: marketSymbol,
    interval: resolution,
    start,
    end,
    limit,
    countBack,
  });
};

export function addTypeToDepositWithdraw(
  item: KatanaPerpsDeposit | KatanaPerpsWithdrawal
) {
  if ('depositId' in item) {
    const deposit: ApiDeposit = item;
    return { ...deposit, type: 'deposit' as const };
  }
  if ('withdrawalId' in item) {
    const withdrawal: ApiWithdrawal = item;
    return { ...withdrawal, type: 'withdrawal' as const };
  }
  // Unreachable
  // return { ...item, time: 0, type: 'unknown' };
  throw new Error('Unknown DepositWithdrawal type');
}

export async function fetchInternalBalanceHistory({
  wallet,
  page,
  limit,
}: {
  wallet: string;
  page: number;
  limit: number;
}): Promise<ApiBalanceHistory> {
  const data = await getChainPrivateApi().getInternalBalanceHistory({
    wallet,
    page,
    limit,
  });
  return {
    count: data.count,
    items: data?.items.map(addTypeToDepositWithdraw),
  };
}

/** https://docs-perps.katana.network/#create-order */
export const createLimitOrder = prepareCreateLimitOrder;
export const createLimitStopOrder = prepareCreateLimitStopOrder;
export const createMarketOrder = prepareCreateMarketOrder;

export const cancelOrder = async (
  orderId: string,
  walletAddress: string,
  signTypedDataFn: TypeDataSignFn,
  delegatedKey: DelegatedKeyAuthorization | undefined
) => {
  const params: RestRequestCancelOrders = {
    nonce: uuidv1(),
    orderIds: [orderId],
    wallet: walletAddress,
    delegatedKey: delegatedKey?.walletAddress || undefined,
  };
  return getChainPrivateApi().cancelOrders(params, signTypedDataFn);
};

/** Similar to {@link cancelOrder} but only for conditional TPSL orders */
export const cancelConditionalTPSLOrder = async ({
  parentOrderId,
  walletAddress,
  signTypedDataFn,
  delegatedKey,
  type,
}: {
  parentOrderId: string;
  walletAddress: string;
  signTypedDataFn: TypeDataSignFn;
  delegatedKey: DelegatedKeyAuthorization | undefined;
  type: 'takeProfit' | 'stopLoss';
}) => {
  const params: RestRequestCancelOrders = {
    nonce: uuidv1(),
    orderIds: [parentOrderId],
    wallet: walletAddress,
    delegatedKey: delegatedKey?.walletAddress || undefined,
  };
  const internalApi = getChainPrivateApi()[INTERNAL_SYMBOL];
  return (
    type === 'takeProfit'
      ? internalApi.cancelConditionalTakeProfitOrders
      : internalApi.cancelConditionalStopLossOrders
  )(params, signTypedDataFn);
};

export const cancelAllOrders = async (
  marketFilter: string | false,
  walletAddress: string,
  signTypedDataFn: TypeDataSignFn,
  delegatedKey: DelegatedKeyAuthorization | undefined
) => {
  const params: RestRequestCancelOrders =
    marketFilter === false
      ? {
          nonce: uuidv1(),
          wallet: walletAddress,
          delegatedKey: delegatedKey?.walletAddress || undefined,
        }
      : {
          nonce: uuidv1(),
          market: marketFilter,
          wallet: walletAddress,
          delegatedKey: delegatedKey?.walletAddress || undefined,
        };
  return getChainPrivateApi().cancelOrders(params, signTypedDataFn);
};

export const fetchFundingPaymentsPaginated = ({
  page,
  pageLimit,
  walletAddress,
  market,
  isVault,
}: {
  page: number;
  pageLimit: number;
  walletAddress: string;
  market?: string;
  isVault: boolean;
}) => {
  const parameters: RestRequestGetFundingPayments = {
    nonce: IGNORED_NONCE,
    wallet: walletAddress,
    page,
    market,
    limit: pageLimit,
  };
  if (isVault) {
    return getChainPrivateApi().getInternalFundingPaymentsForVault(parameters);
  }
  return getChainPrivateApi()[INTERNAL_SYMBOL].getFundingPayments(parameters);
};

export const fetchDeposits = (params: {
  wallet: string;
  limit?: number;
}): Promise<ApiDeposit[]> =>
  getChainPrivateApi().getDeposits({
    nonce: IGNORED_NONCE,
    ...params,
  });

export const fetchWithdrawal = (
  walletAddress: string,
  withdrawalId: string
): Promise<ApiWithdrawal> =>
  getChainPrivateApi().getWithdrawal({
    nonce: IGNORED_NONCE,
    wallet: walletAddress,
    withdrawalId,
  });

/** Withdraw without bridging */
export const withdrawNativeUsdc = (
  wallet: IWallet,
  quantity: string,
  maximumGasFee: number
): Promise<unknown> => {
  clearGlobalEnforcedChainId();
  return getChainPrivateApi().withdraw(
    {
      nonce: uuidv1(),
      quantity: Number(quantity).toFixed(8),
      maximumGasFee: Number(maximumGasFee).toFixed(8),
      wallet: wallet.walletAddress,
      bridgeAdapterAddress: ETH_ADDRESS_ZERO,
      bridgeAdapterPayload: '0x',
    },
    (...typedParams) => signTypedData(wallet, undefined, typedParams)
  );
};

/**
 * Withdraw and bridge to different chain
 * https://github.com/idexio/idex-sdk-js-ikon/blob/beta/src/bridge.ts#L122
 */
export const withdrawAndBridgeUsdc = (
  wallet: IWallet,
  quantity: string,
  maximumGasFee: number,
  targetChain: ChainsFromConfig
): Promise<unknown> => {
  clearGlobalEnforcedChainId();
  if (targetChain === 'katanaChain') {
    throw new Error('Native chain used for bridging');
  }
  const { layerZeroEndpointId: targetChainLayerZeroEndpointId } =
    getStargateBridgeConfig(targetChain);
  if (!targetChainLayerZeroEndpointId) {
    throw new Error('Missing bridge configuration, please contact support.');
  }
  return getChainPrivateApi().withdraw(
    {
      nonce: uuidv1(),
      quantity: Number(quantity).toFixed(8),
      maximumGasFee: maximumGasFee.toFixed(8),
      wallet: wallet.walletAddress,
      bridgeAdapterAddress: config.chains.katanaChain.stargateAdapterV3Address,
      bridgeAdapterPayload: encodeWithdrawalBridgeAdapterPayload({
        layerZeroEndpointId: targetChainLayerZeroEndpointId,
      }),
    },
    (...typedParams) => signTypedData(wallet, undefined, typedParams)
  );
};

/**
 * Withdraw from Exchange balance and Deposit to specified Vault
 */
export const withdrawFromExchangeAndBridgeUsdcToVaultUsingLoopback = (
  wallet: IWallet,
  quantity: string,
  maximumGasFee: number,
  managerWallet: string
): Promise<unknown> => {
  const { exchangeVaultLoopbackAdapterV1Address, stargateAdapterV3Address } =
    config.chains.katanaChain;
  if (!exchangeVaultLoopbackAdapterV1Address) {
    throw new Error('Missing loopback configuration, please contact support.');
  }
  const bridgeAdapterPayload = encodeDepositBridgeAdapterPayload(
    getStargateBridgeConfig('katanaChain'),
    {
      exchangeLayerZeroAdapterAddress: stargateAdapterV3Address,
      // TODO: vaults hardcoded value
      minimumForwardQuantityMultiplierInPips: BigInt('99900000'),
      quantityInAssetUnits: BigInt(
        BigNumberJs(quantity)
          .multipliedBy(10 ** DEFAULT_USDC_DECIMALS)
          .toString()
      ),
      sourceBridgeTarget: 'katana.katana',
      bridgePayloadType:
        DepositBridgeAdapterPayloadType.depositToManagedAccount,
      fixedIncomeVaultProviderAddress:
        config.chains.katanaChain.fixedIncomeVaultProviderAddress,
      depositorWallet: wallet.walletAddress,
      managerWallet,
    } as any // TODO: katana vaults
  );
  return getChainPrivateApi().withdraw(
    {
      nonce: uuidv1(),
      quantity: Number(quantity).toFixed(8),
      maximumGasFee: maximumGasFee.toFixed(8),
      wallet: wallet.walletAddress,
      bridgeAdapterAddress: exchangeVaultLoopbackAdapterV1Address,
      bridgeAdapterPayload,
    },
    (...typedParams) => signTypedData(wallet, undefined, typedParams)
  );
};

export const fetchPostReferralCode = (
  walletAddress: string,
  referralCode: string
) =>
  getChainPrivateApi()
    .postInternalReferralsRedeemRequest(walletAddress, referralCode)
    .catch(error => {
      console.error('Failed to postInternalReferralsRedeemRequest ', error);
      throw error;
    });

export const fetchWallet24hProfitAndLoss = async (walletAddress: string) => {
  const now = Date.now();
  const yesterday = new Date(now - 24 * 60 * 60 * 1000).getTime();

  const response = await getChainPrivateApi().getHistoricalPnL({
    wallet: walletAddress,
    start: yesterday,
    end: now,
    nonce: IGNORED_NONCE,
  });

  // API only provide 1h interval records for 24h
  // const last24hPnLAmount = response.reduce((sum, record) => {
  //   sum += Number(record.totalPnL);
  //   console.log(new Date(record.time));
  //   return sum;
  // }, 0);

  if (response.length === 0) {
    return {
      firstTotalPnL: 0,
      lastTotalPnl: 0,
      firstNonZeroEquity: 0,
      lastEquity: 0,
    };
  }

  const first = response[0];
  const firstNonZero =
    Number(response.find(record => Number(record.equity) > 0)?.equity) || 0;
  const last = response[response.length - 1];
  return {
    firstTotalPnL: Number(first.totalPnL),
    lastTotalPnl: Number(last.totalPnL),
    firstNonZeroEquity: firstNonZero,
    lastEquity: Number(last.equity),
    // firstNetDepositsWithdrawals: Number(first?.netDepositsWithdrawals),
  };
};

export const fetchWallet7dHistoricalPnL = async (
  walletAddress: string,
  isVault: boolean
) => {
  const now = Date.now();
  const days = 7;
  const params: RestRequestGetHistoricalPnL = {
    wallet: walletAddress,
    start: now - 1000 * 60 * 60 * 24 * days,
    end: now,
    nonce: IGNORED_NONCE,
  };
  return isVault
    ? getChainPrivateApi().getInternalHistoricalPnLForVault(params)
    : getChainPrivateApi().getHistoricalPnL(params);
};

export const fetchMarketMakerRewardsEpochs = () =>
  getChainPrivateApi().getMarketMakerRewardsEpochs();

/**
 * Get current epoch or `null` if undefined and prevent 404
 * which would cause with caching in react-query.
 */
// export const fetchCurrentMarketMakerRewardsEpoch = (walletAddress?: string) =>
//   (walletAddress
//     ? getChainPrivateApi().getMarketMakerRewardsEpoch({
//         wallet: walletAddress,
//         nonce: IGNORED_NONCE,
//       })
//     : getChainPrivateApi().getMarketMakerRewardsEpoch({ nonce: IGNORED_NONCE })
//   ).catch(error => {
//     if (error.idexErrorCode === API_ERROR_EPOCH_NOT_FOUND) {
//       console.log(
//         'API could not find current epoch - Expected if no epoch is running'
//       );
//       // To make API type-safe we return `null` when nothing was found
//       return null;
//     }
//     throw error;
//   });

export const fetchMarketMakerRewardsEpoch = ({
  walletAddress,
  epochId,
}: {
  walletAddress?: string;
  /** Epoch Id, or undefined for current */
  epochId: string;
}) =>
  walletAddress
    ? getChainPrivateApi().getMarketMakerRewardsEpoch({
        wallet: walletAddress,
        epochId,
        nonce: IGNORED_NONCE,
      })
    : getChainPrivateApi().getMarketMakerRewardsEpoch({
        epochId,
        nonce: IGNORED_NONCE,
      });

export const fetchPayouts = (wallet: string, program: PayoutProgramCode) =>
  getChainPrivateApi().getPayouts({
    nonce: IGNORED_NONCE,
    // TODO: payouts sdk - not type safe anymore
    program: program as any,
    wallet,
  });

export const authorizePayout = (wallet: string, program: PayoutProgramCode) =>
  getChainPrivateApi().authorizePayout({
    nonce: IGNORED_NONCE,
    // TODO: payouts sdk - not type safe anymore
    program: program as any,
    wallet,
  });

export function setInitialMarginFractionOverride({
  market,
  walletAddress,
  delegatedKeyAddress,
  value,
  signer,
}: {
  market: string;
  walletAddress: string;
  delegatedKeyAddress: string | undefined;
  value: number | string;
  signer: SignTypedData;
}) {
  const coreParams = {
    market,
    nonce: uuidv1(),
    wallet: walletAddress,
    initialMarginFractionOverride: Number(value).toFixed(EXCHANGE_DECIMALS),
  };
  return getMainPrivateApi().setInitialMarginFractionOverride(
    delegatedKeyAddress
      ? { ...coreParams, delegatedKey: delegatedKeyAddress }
      : coreParams,
    signer
  );
}

export async function setInternalVaultDetails(
  params: Omit<RestRequestSetVaultDetails, 'nonce'>,
  wallet: IWallet,
  delegatedAuthorization: DelegatedKeyAuthorization
) {
  const coreParams = {
    description: params.description,
    name: params.name,
    nonce: uuidv1(),
    wallet: params.wallet,
  };
  return getMainPrivateApi()[INTERNAL_SYMBOL].setVaultDetails(
    delegatedAuthorization
      ? { ...coreParams, delegatedKey: delegatedAuthorization.walletAddress }
      : coreParams,
    (...params) =>
      signTypedData(
        wallet,
        delegatedAuthorization.walletAddress
          ? delegatedAuthorization
          : undefined,
        params
      )
  );
}

export const getXChallengeCode = ({
  walletAsManager,
}: {
  walletAsManager: string;
}) =>
  getMainPrivateApi()
    [INTERNAL_SYMBOL].getXChallenge({
      manager: walletAsManager,
      wallet: walletAsManager,
      nonce: uuidv1(),
    })
    .then(response => response.challenge);

export const removeXConnection = ({
  walletAsManager,
  signer,
}: {
  walletAsManager: string;
  signer: SignTypedData;
}) =>
  getMainPrivateApi()[INTERNAL_SYMBOL].removeVaultXConnection(
    {
      manager: walletAsManager,
      wallet: walletAsManager,
      nonce: uuidv1(),
    },
    signer
  );

export const setXConnection = ({
  walletAsManager,
  callbackCode: xCallbackCode,
  signer,
}: {
  walletAsManager: string;
  callbackCode: string;
  signer: SignTypedData;
}) =>
  getMainPrivateApi()[INTERNAL_SYMBOL].setVaultXConnection(
    {
      manager: walletAsManager,
      wallet: walletAsManager,
      nonce: uuidv1(),
      code: xCallbackCode,
    },
    signer
  );

// Promise.resolve([
//   {
//     symbol: 'DIL',
//     name: 'Dileep Coin',
//     contractAddress: '0xe48707b360357fb47a2ae535f4bf0b9452fd6d48',
//     exchangeDecimals: 18,
//     assetDecimals: 18,
//   } as any,
//   {
//     symbol: 'JUR',
//     name: 'Juraj Coin',
//     contractAddress: '0x398494cd8a8e29c0c65f85dc3f35b6556433255d',
//     exchangeDecimals: 18,
//     assetDecimals: 18,
//   } as any,
//   {
//     symbol: 'JAS',
//     name: 'Jason Coin',
//     contractAddress: '0xe692aad209432bb6c78cf02520502f0b2f3d4e8d',
//     exchangeDecimals: 18,
//     assetDecimals: 18,
//   } as any,
//   {
//     symbol: 'PIP',
//     name: 'Dickens Coin',
//     contractAddress: '0xb9a118bb015b29fd8d93f9969caca699b92518e8',
//     exchangeDecimals: 8,
//     assetDecimals: 18,
//   } as any,
//   {
//     symbol: 'CUR',
//     name: 'Dollar Stable Coin',
//     contractAddress: '0xfaa4052f08429a43fae55564394aa6c0379a5728',
//     exchangeDecimals: 2,
//     assetDecimals: 18,
//   } as any,
// ]);
